import React from "react"
import {
  incomeFromProfile,
  formatMoney,
  personDisplayName,
  roleLabelForPerson,
} from "./api"

function getRow(label1, d1, label2, d2, classname) {
  return (
    <div className={classname ? classname + " row" : "row"}>
      <div className="col px-0" style={{ marginTop: 22 }}>
        <div className=" newLabel">{label1}</div>
        <div className="newLabelText">{d1}</div>
      </div>
      <div className="col" style={{ marginTop: 22 }}>
        <div className="newLabel">{label2 ? label2 : ""}</div>
        <div className="newLabelText">{d2 ? d2 : ""}</div>
      </div>
    </div>
  )
}

function renderIncomeApplicantRole(roleLabel, person, showName) {
  const name = personDisplayName(person)
  return (
    <div className="loan-overview-applicant-role loan-overview-income-role">
      <span className="loan-overview-applicant-role-label">{roleLabel}</span>
      {showName ? (
        <>
          <span className="loan-overview-applicant-role-separator" aria-hidden="true">
            —
          </span>
          <span
            className={
              name
                ? "loan-overview-applicant-name"
                : "loan-overview-applicant-name loan-overview-applicant-name--empty"
            }
          >
            {name || "Name not provided"}
          </span>
        </>
      ) : null}
    </div>
  )
}

export default function ApplicantsCardV2({ applicants = [] }) {
  if (!applicants.length) {
    return <div>Applicant information has not been provided yet.</div>
  }

  const coborrowerCount = applicants.filter(
    (p) => !(p.isPrimary || p.role === "borrower")
  ).length
  let coborrowerIndex = 0

  return applicants.map((person) => {
    const isPrimary = person.isPrimary || person.role === "borrower"
    if (!isPrimary) coborrowerIndex += 1
    const roleLabel = roleLabelForPerson(person, coborrowerCount, coborrowerIndex)
    const firstName = person.firstName || ""
    const lastName = person.lastName || ""

    if (!firstName && !lastName) {
      return (
        <div className="loan-overview-applicant" key={person.partyId || roleLabel}>
          <div className="loan-overview-applicant-role">{roleLabel}</div>
          <div className="loan-overview-applicant-empty">
            {roleLabel} information has not been provided yet.
          </div>
        </div>
      )
    }

    return (
      <div className="loan-overview-applicant" key={person.partyId || roleLabel}>
        <div className="loan-overview-applicant-role">{roleLabel}</div>
        {getRow("First name", firstName || "-", "Last name", lastName || "-")}
        {getRow(
          "Address",
          person.address || "-",
          "Citizenship",
          person.citizenship || "-",
          "mt-2"
        )}
      </div>
    )
  })
}

export function IncomeCardV2({ income = [] }) {
  if (!income.length) {
    return <div>Employment information has not been provided yet.</div>
  }

  const coborrowerCount = income.filter(
    (p) => !(p.isPrimary || p.role === "borrower")
  ).length
  const showCoborrowerNames = income.length > 1
  let coborrowerIndex = 0

  return income.map((person) => {
    const isPrimary = person.isPrimary || person.role === "borrower"
    if (!isPrimary) coborrowerIndex += 1
    const roleLabel = roleLabelForPerson(person, coborrowerCount, coborrowerIndex)
    const calc = incomeFromProfile(person.profile)
    const blocks = []

    if (calc.hasOccupation === null) {
      blocks.push(
        <div key="empty" className="loan-overview-applicant-empty">
          {roleLabel} information has not been provided yet.
        </div>
      )
    } else {
      switch (calc.hasOccupation) {
        case "unemployed":
        default:
          blocks.push(getRow("Employment", "Unemployed"))
          break
        case "retired":
          blocks.push(getRow("Employment", "Retired"))
          break
        case "employed":
          blocks.push(
            <div key="employed">
              {getRow(
                "Employer",
                calc.occupation.employername || "-",
                "Position",
                calc.occupation.positiontitle || "-",
                "mb-2"
              )}
              {getRow(
                "Monthly income (or loss)",
                formatMoney(calc.employment, 0)
              )}
            </div>
          )
          if (calc.otherOccupations && calc.otherOccupations.length > 0) {
            blocks.push(
              <div className="title-5-bold" key="also">
                Also employed at:
              </div>
            )
            calc.otherOccupations.forEach((o, i) => {
              blocks.push(
                <div key={`other-${i}`}>
                  <b>{o.employername}</b>, at {o.employeraddress} as{" "}
                  <b>{o.positiontitle}</b>
                </div>
              )
            })
          }
          break
      }
      if (calc.dividends > 0) {
        blocks.push(
          <div key="div">
            Gross <b>monthly</b> dividends:{" "}
            <b>{formatMoney(calc.dividends)}</b> (yearly dividends of{" "}
            <b>{formatMoney(calc.dividends * 12)}</b>)
          </div>
        )
      }
      if (calc.other > 0) {
        blocks.push(
          <div key="other-inc">
            Gross <b>monthly</b> other income:{" "}
            <b>{formatMoney(calc.other)}</b> (yearly amount of{" "}
            <b>{formatMoney(calc.other * 12)}</b>)
          </div>
        )
      }
    }

    return (
      <div className="loan-overview-applicant" key={person.partyId || roleLabel}>
        {renderIncomeApplicantRole(roleLabel, person, showCoborrowerNames && !isPrimary)}
        <div className="mt-3">{blocks}</div>
      </div>
    )
  })
}

export function AssetsCardV2({ assets }) {
  const banks = assets?.banks || []
  const brokerages = assets?.brokerages || []
  const gifts = assets?.gifts || []
  const otherAssets = assets?.otherAssets || []

  const sumBuckets = (buckets) =>
    buckets.reduce((sum, b) => sum + (Number(b.amount) || 0), 0)

  const rows = [
    { title: "Bank balances", amount: sumBuckets(banks) },
    { title: "Brokerage accounts balances", amount: sumBuckets(brokerages) },
    { title: "Gifts", amount: sumBuckets(gifts) },
    { title: "Other assets", amount: sumBuckets(otherAssets) },
  ].filter((row) => row.amount > 0)

  if (!rows.length) {
    return <div>No assets was added.</div>
  }

  const pairs = []
  for (let i = 0; i < rows.length; i += 2) {
    const left = rows[i]
    const right = rows[i + 1]
    pairs.push(
      getRow(
        left.title,
        formatMoney(left.amount),
        right ? right.title : "",
        right ? formatMoney(right.amount) : "",
        i === 0 ? "mt-3" : "mt-2"
      )
    )
  }
  return pairs
}

export function ExpensesCardV2({ expenses }) {
  if (!expenses?.hasExistingMortgages) {
    return <div>No existing mortgages.</div>
  }
  return (
    <div className="mt-3">
      {getRow(
        "Monthly mortgage payments",
        formatMoney(expenses.monthlyMortgagePayments),
        "Total balance",
        formatMoney(expenses.totalBalance)
      )}
    </div>
  )
}

const manufacturedHomeTypeMap = {
  SingleWide: "Single Wide",
  DoubleWide: "Double Wide",
  TripleWide: "Triple Wide",
}

const parkLocationMap = {
  Park: "Park",
  LandorHome: "Land/home",
  CoopPark: "Co-op park",
}

function purposeLabel(loan) {
  const isPreapproval =
    loan?.hasContract === false && loan?.purpose === "purchase"
  if (isPreapproval) return "preapproval application"
  switch (loan?.purpose) {
    case "purchase":
      return "Purchase"
    case "refinance":
      return "Refinance"
    case "cashoutrefinance":
      return "Cash-out refinance"
    default:
      return "TBD"
  }
}

function propertyTypeLabel(propertyType) {
  switch (propertyType) {
    case "singlefamilyattached":
      return "Single family attached"
    case "singlefamilydetached":
      return "Single family detached"
    case "condominium":
      return "Condominium attached"
    case "condominiumdetached":
      return "Condominium detached"
    case "pudattached":
      return "PUD attached"
    case "puddetached":
      return "PUD detached"
    case "twotofourfamily":
      return "2 to 4 Unit Multifamily"
    case "commercial":
      return "5+ Unit Multifamily"
    case "mobilehome":
      return "Manufactured home"
    default:
      return "Unknown property type"
  }
}

function occupancyLabel(occupancy) {
  switch (occupancy) {
    case "principal":
      return "Primary residence"
    case "secondhome":
      return "Second home"
    case "investment":
      return "Investment property"
    default:
      return "-"
  }
}

export function LoanCardV2({ loan }) {
  if (!loan || (loan.appraisal === "" && loan.salesPrice === "")) {
    return <div>Loan information has not been provided yet.</div>
  }

  const isPreapproval =
    loan.hasContract === false && loan.purpose === "purchase"
  const address = isPreapproval ? "TBD" : loan.address || "-"
  const salesPrice = Number(loan.salesPrice) || 0
  const downPayment = Number(loan.downPayment) || 0
  const loanSize = Number(loan.loanSize) || 0
  const paydown = Number(loan.paydown) || 0
  const cashOut = Number(loan.cashOut) || 0
  const purpose = loan.purpose || ""

  // Prefer selected-product loanSize (same as Overview header). Purchase may
  // fall back to price - down payment before a product is chosen.
  const loanAmount =
    loanSize ||
    (purpose === "purchase" && downPayment ? salesPrice - downPayment : 0)

  let secondaryLabel = ""
  let secondaryValue = ""
  if (purpose === "purchase" && downPayment) {
    secondaryLabel = "Down payment"
    secondaryValue = formatMoney(downPayment, 0)
  } else if (
    (purpose === "refinance" || purpose === "ratetermrefinance") &&
    paydown
  ) {
    secondaryLabel = "Cash to close"
    secondaryValue = formatMoney(paydown, 0)
  } else if (purpose === "cashoutrefinance" && cashOut) {
    secondaryLabel = "Cash out"
    secondaryValue = formatMoney(cashOut, 0)
  }

  return (
    <div className="mt-3">
      {getRow("Loan purpose", purposeLabel(loan), "Property location", address)}
      {getRow(
        "Property type",
        propertyTypeLabel(loan.propertyType),
        "Occupancy",
        occupancyLabel(loan.occupancy),
        "mt-2"
      )}
      {getRow(
        "Expected home value",
        formatMoney(loan.appraisal, 0),
        "Purchase price",
        formatMoney(loan.salesPrice, 0),
        "mt-2"
      )}
      {!!loanAmount &&
        getRow(
          "Loan amount",
          formatMoney(loanAmount, 0),
          secondaryLabel,
          secondaryValue,
          "mt-2"
        )}
    </div>
  )
}

export function MobileHomeCardV2({ loan }) {
  if (loan?.propertyType !== "mobilehome") return null
  const mh = loan.mobileHomeInfo || {}
  const debts = Array.isArray(mh.debtsToBePaid) ? mh.debtsToBePaid : []

  const getWidth = () => {
    if (!mh.widthft) return "-"
    const inch = mh.widthinch ? ` ${mh.widthinch} inch` : ""
    return `${mh.widthft} ft${inch}`
  }
  const getLength = () => {
    if (!mh.lengthft) return "-"
    const inch = mh.lengthinch ? ` ${mh.lengthinch} inch` : ""
    return `${mh.lengthft} ft${inch}`
  }

  return (
    <div className="mt-3">
      {getRow(
        "Manufactured home park full name",
        mh.mobilehomeparkfullname || "-",
        "Monthly space rent",
        formatMoney(mh.leasedSpaceRentAmount, 0)
      )}
      {getRow("Park Address", mh.parkAddress || "-", "Space number", mh.space || "-")}
      {getRow(
        "Year built",
        mh.year || "-",
        "Type",
        manufacturedHomeTypeMap[mh.mobilehometype] || "-",
        "mt-2"
      )}
      {getRow("Width", getWidth(), "Length", getLength(), "mt-2")}
      {getRow(
        "Manufacturer make",
        mh.manufacturerMake || "-",
        "Manufacturer model",
        mh.manufacturerModel || "-"
      )}
      {getRow(
        "Condition",
        mh.mobilehomedescription || "-",
        "Location",
        parkLocationMap[mh.mobilehomelocation] || "-"
      )}
      {loan.purpose === "ratetermrefinance" &&
        getRow(
          "Original purchase price",
          formatMoney(mh.originalpurchaseprice, 0),
          "",
          "",
          "mt-2"
        )}
      {loan.purpose === "cashoutrefinance" &&
        getRow(
          "Original purchase price",
          formatMoney(mh.originalpurchaseprice, 0),
          "Cash out amount requested",
          formatMoney(mh.cashOutRequested, 0),
          "mt-2"
        )}
      {debts.length > 0 && (
        <div>
          <div className="title-5-bold mt-2">Debts to be paid:</div>
          <ul>
            {debts.map((d, i) => (
              <li key={i}>
                {d.description}: {formatMoney(d.amount, 0)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export function applicantsTitleV2(applicants = []) {
  return applicants.length > 1 ? "Applicants" : "Applicant"
}

export { personDisplayName }
